import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { parseApiError, useAuth, usePermission } from '@saas-kit/lib';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorBanner,
  Input,
  PageHeader,
  toast,
} from '@saas-kit/ui';

import { getDownloadUrl, listFiles, uploadFile } from './api';
import { TenantRequiredState } from '../../components/TenantRequiredState';

export function FilesPage(): JSX.Element {
  const { t } = useTranslation('files');
  const selectedTenant = useAuth((s) => s.selectedTenant);
  const canRead = usePermission('files.read');
  const canUpload = usePermission('files.upload');
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const filesQuery = useQuery({
    queryKey: ['tenant-files', selectedTenant?.tenant_id],
    queryFn: listFiles,
    enabled: Boolean(selectedTenant?.tenant_id),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadFile(file),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tenant-files'] });
      setSelectedFile(null);
      toast.success(t('toasts.uploaded'));
    },
    onError: (error) => toast.error(t(parseApiError(error).i18nKey)),
  });

  const downloadMutation = useMutation({
    mutationFn: getDownloadUrl,
    onSuccess: ({ url }) => {
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    onError: (error) => toast.error(t(parseApiError(error).i18nKey)),
  });

  const files = filesQuery.data ?? [];
  const totalBytes = files.reduce((sum, file) => sum + file.size_bytes, 0);

  return (
    <div className="space-y-6">
      <PageHeader title={t('title')} description={t('subtitle')} />

      <div className="grid gap-4 md:grid-cols-3">
        <MetricCard label={t('metrics.total')} value={String(files.length)} />
        <MetricCard label={t('metrics.storage')} value={formatSize(totalBytes)} />
        <MetricCard
          label={t('metrics.upload_access')}
          value={canUpload ? t('metrics.upload_access_yes') : t('metrics.upload_access_no')}
        />
      </div>

      {!selectedTenant ? (
        <TenantRequiredState
          title={t('tenant_required.title')}
          body={t('tenant_required.body')}
          cta={t('tenant_required.cta')}
        />
      ) : !canRead ? (
        <ErrorBanner>{t('errors.read_denied')}</ErrorBanner>
      ) : (
        <>
          {canUpload ? (
            <Card>
              <CardHeader>
                <CardTitle>{t('upload.title')}</CardTitle>
                <CardDescription>{t('upload.description')}</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-4 md:flex-row md:items-end">
                <div className="flex-1 space-y-2">
                  <label htmlFor="tenant-file" className="text-sm font-medium text-foreground">
                    {t('upload.file_label')}
                  </label>
                  <Input
                    id="tenant-file"
                    type="file"
                    onChange={(event) => setSelectedFile(event.target.files?.[0] ?? null)}
                  />
                </div>
                <Button
                  onClick={() => selectedFile && uploadMutation.mutate(selectedFile)}
                  disabled={!selectedFile || uploadMutation.isPending}
                >
                  {uploadMutation.isPending ? t('upload.uploading') : t('upload.submit')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>{t('upload.unavailable_title')}</CardTitle>
                <CardDescription>{t('upload.unavailable_body')}</CardDescription>
              </CardHeader>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>{t('list.title')}</CardTitle>
              <CardDescription>{t('list.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              {filesQuery.isError ? (
                <ErrorBanner>{t(parseApiError(filesQuery.error).i18nKey)}</ErrorBanner>
              ) : files.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="border-b border-border text-start text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 font-medium">{t('list.columns.name')}</th>
                        <th className="px-3 py-2 font-medium">{t('list.columns.type')}</th>
                        <th className="px-3 py-2 font-medium">{t('list.columns.size')}</th>
                        <th className="px-3 py-2 font-medium">{t('list.columns.created_at')}</th>
                        <th className="px-3 py-2 font-medium">{t('list.columns.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {files.map((file) => (
                        <tr key={file.id} className="border-b border-border/70">
                          <td className="px-3 py-3">{file.name}</td>
                          <td className="px-3 py-3">{file.content_type}</td>
                          <td className="px-3 py-3">{formatSize(file.size_bytes)}</td>
                          <td className="px-3 py-3">{new Date(file.created_at).toLocaleString()}</td>
                          <td className="px-3 py-3">
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={downloadMutation.isPending}
                              onClick={() => downloadMutation.mutate(file.id)}
                            >
                              {t('list.download')}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : filesQuery.isLoading ? (
                <p className="text-sm text-muted-foreground">{t('loading')}</p>
              ) : (
                <EmptyState
                  title={t('list.empty_title')}
                  description={t('list.empty')}
                />
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Card>
      <CardContent className="p-5">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-2 text-2xl font-semibold text-foreground">{value}</p>
      </CardContent>
    </Card>
  );
}

function formatSize(sizeBytes: number): string {
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}
